import { identify, Identify, init, setDeviceId, setUserId, track } from "@amplitude/analytics-browser";
import trackService from "@/api/services/trackService";
import i18n from "@/i18n";
import router from "@/router/router";
import { isIframe } from "@/utils/domHelper";
import { Video } from "@/interfaces/Video/Video";
import { Tag } from "@/interfaces/Tags/Tag";
import { useVisitStore } from "@/store/visit";
import { Visit } from "@/interfaces/Visit.ts";
import { useUserStore } from "@/store/user.ts";
import { useVideoStore } from "@/store/video.ts";
import { usePlayerStore } from "@/store/player.ts";

const retryLimit = 2;
let isInitialized = false;
let previousIdentifyObj: object | null = null;

export const amplitudeTrack = async (name: string, props: Record<string, any> = {}, count = 0) => {
  if (!isInitialized && import.meta.env.VITE_AMPLITUDE_API_KEY) {
    init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
      fetchRemoteConfig: false, //new property doesnt work for disabling
      remoteConfig: {
        fetchRemoteConfig: false,
      },
    });
    isInitialized = true;
  } else if (!import.meta.env.VITE_AMPLITUDE_API_KEY) {
    isInitialized = true;
    return new Promise<void>((resolve) => {
      console.debug(`track: ${name}`, props);
      resolve();
    });
  }

  const identifyObj: Record<string, any> = {};
  const visit = useVisitStore().getVisit || ({} as Visit);

  props.iframe = isIframe();
  props.site_version = "beta";
  props.URL = window.location.href;
  props["Application language"] = i18n.global.locale;
  props["Is premium"] = "null";
  props.browser = visit.browser;
  props.country = visit.country;
  props.campaign = visit.campaign || "";
  props.device = visit.is_mobile ? "mobile" : "desktop";
  props.app_version = import.meta.env.VITE_VERSION;
  props.app_date = import.meta.env.VITE_DATE;
  props.region = visit.region;
  props.current_region = visit.current_region;

  const userStore = useUserStore();
  const profile = userStore.getProfile;

  if (profile) {
    const userId = `00000${profile.id}`;
    setUserId(userId);
    props.user_id = userId;
    props["Is premium"] = userStore.isPremium !== null;

    if (profile.features) {
      Object.keys(profile?.features).forEach((key) => {
        const feature = profile?.features[key];
        const version = feature?.version ?? "true";
        let settings = feature?.settings ?? {};
        settings = Array.isArray(settings) ? settings.reduce((a, v) => ({ ...a, [v]: v }), {}) : settings;

        identifyObj[key] = version;
        identifyObj[`${key}_settings`] = settings;
        props[key] = version;
        props[`${key}_settings`] = settings;
      });
    }

    const subscription = profile.subscription;

    if (subscription !== null) {
      identifyObj.Plan = subscription.type.name;
      identifyObj["Plan token"] = subscription.type.token;
    }

    identifyObj.age = profile.age;
    identifyObj.source = profile.source;
    identifyObj.other_source = profile.other_source;
    identifyObj.registration_campaign = profile.registration_campaign?.id;
    identifyObj.registration_region = profile.registration_region;
    identifyObj["Is premium"] = userStore.isPremium !== null;
    identifyObj["Is team member"] = profile.is_team_member;
    identifyObj["Model ID"] = profile.model_id;

    if (JSON.stringify(identifyObj) !== JSON.stringify(previousIdentifyObj) && profile) {
      previousIdentifyObj = identifyObj;
      const newIdentify = new Identify();

      for (const key in identifyObj) {
        newIdentify.set(key, identifyObj[key]);
      }

      identify(newIdentify);
    }
  }

  if (visit.id) {
    const deviceId = `00000${visit.id}`;
    setDeviceId(deviceId);
    props.device_id = deviceId;
  }

  const visitFeatures = visit.features;

  if (visitFeatures) {
    Object.keys(visitFeatures).forEach((key) => {
      const feature = visitFeatures[key];

      props[key] = feature?.version ?? "true";
      let settings = feature?.settings ?? {};
      settings = Array.isArray(settings) ? settings.reduce((a, v) => ({ ...a, [v]: v }), {}) : settings;

      props[`${key}_settings`] = settings;
    });
  }

  const response = await track(name, props).promise;
  const code = response?.code || 0;

  if (code >= 400) {
    if (count >= retryLimit) {
      return;
    }

    await amplitudeTrack(name, props, count + 1);
  } else if (code === 0) {
    await trackService.track(name, props);
  }
};

export const amplitudePageViewTrack = async (props: Record<string, any> = {}) => {
  await amplitudeTrack("Page view", props);
};

export const amplitudeButtonClickTrack = async (buttonName: string, entityId: number | null = null, props: Record<string, any> = {}) => {
  props["Button name"] = buttonName;

  if (entityId) {
    props["Entity ID"] = entityId;
  }

  await amplitudeTrack("Button click", props);
};

export const amplitudeRegistrationButtonClickTrack = async (location: string, props: Record<string, any> = {}) => {
  props.location = location;
  await amplitudeTrack("Register button click", props);
};

export const amplitudeTopMenuClickTrack = async (navItemName: string, props: Record<string, any> = {}) => {
  props["Navigation item"] = navItemName;
  await amplitudeTrack("Top-menu click", props);
};

export const amplitudeFavoriteClickTrack = async (navItemName: string, props: Record<string, any> = {}) => {
  props["Navigation item"] = navItemName;
  await amplitudeTrack("Favourite menu click", props);
};

export const amplitudeCarouselTrack = async (carouselName: string) => {
  await amplitudeTrack(`${carouselName}: slide changed`);
};

export const amplitudeVideoTrack = async (eventName: string, id: number | string, props: Record<string, any> = {}) => {
  props.id = id;
  const video: Video = useVideoStore().getVideo(id) || null;

  if (video !== null) {
    props["Participated models"] = video.participated_models?.map(({ name_en }) => name_en);
    props.Tags = video.tags?.map(({ name_en }) => name_en);
    props.trailer = video.tags ? video.tags.some((tag: Tag) => tag.name_en === "Trailer") : false;
  }

  props["Pip Status"] = usePlayerStore().isPipModeEnabled ? "Enabled" : "Disabled";

  if (!props.Location) {
    props.Location = router.currentRoute.value.path;
  }

  await amplitudeTrack(eventName, props);
};
